import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  ArrowLeft, Check, Copy, ExternalLink, Link2,
  AlertTriangle, Upload, Trash2, RefreshCw
} from 'lucide-react'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import { Switch } from '../ui/switch'
import { toast } from 'react-hot-toast'
import { nsConfirm } from '../shared/ns-confirm'

function Field({ label, hint, children, full }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto', display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-soft)' }}>
        {label}{hint && <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}> · {hint}</span>}
      </label>
      {children}
    </div>
  )
}

function CardSection({ title, desc, right, children, danger }) {
  return (
    <div className="card card-pad" style={danger ? { border: '1px solid rgba(255,107,107,0.3)' } : {}}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
        <div style={{ flex: 1 }}>
          <h2 className="h2" style={danger ? { color: 'var(--danger)' } : {}}>{title}</h2>
          {desc && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>{desc}</div>}
        </div>
        {right}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
    </div>
  )
}

function NSToggle({ on, onChange }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onChange(!on) }}
      style={{
        width: 34, height: 19, borderRadius: 999, flexShrink: 0,
        background: on ? 'var(--accent)' : 'var(--surface-2)',
        position: 'relative', transition: 'background 0.15s ease',
        border: '1px solid var(--border)',
      }}
    >
      <span style={{
        position: 'absolute', top: 1, left: on ? 16 : 1,
        width: 15, height: 15, borderRadius: '50%', background: 'white',
        transition: 'left 0.15s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
      }} />
    </button>
  )
}

const SiteSettings = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isSaving, setIsSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const [faviconFile, setFaviconFile] = useState(null)
  const [faviconPreview, setFaviconPreview] = useState(null)
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)

  const { data: site, isLoading, error } = useQuery(
    ['site', id],
    () => apiService.get(`/sites/${id}`),
    { enabled: !!id }
  )

  const [settings, setSettings] = useState({
    maintenanceMode: false,
    maintenanceMessage: 'This website is currently under maintenance. Please check back later.',
    passwordProtection: false,
    password: '',
    searchIndexing: true,
    customCode: { headerCode: '', footerCode: '', analyticsCode: '' }
  })

  const [gaId, setGaId] = useState('')

  useEffect(() => {
    if (site) {
      const meta = site?.data?.meta_data || {}
      setSettings({
        maintenanceMode: meta.maintenanceMode || false,
        maintenanceMessage: meta.maintenanceMessage || 'This website is currently under maintenance. Please check back later.',
        passwordProtection: meta.passwordProtection || false,
        password: meta.accessPassword || '',
        searchIndexing: meta.searchIndexing !== false,
        customCode: {
          headerCode: meta.customCode?.headerCode || '',
          footerCode: meta.customCode?.footerCode || '',
          analyticsCode: meta.customCode?.analyticsCode || ''
        }
      })
      setGaId(meta.gaId || '')
    }
  }, [site])

  const updateSettingsMutation = useMutation(
    (newSettings) => apiService.put(`/sites/${id}/settings`, newSettings),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['site', id])
        queryClient.invalidateQueries('sites')
        toast.success('Site settings updated')
        setIsSaving(false)
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update settings')
        setIsSaving(false)
      }
    }
  )

  const uploadFaviconMutation = useMutation(
    (formData) => apiService.post('/clients/favicon', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    {
      onSuccess: () => {
        toast.success('Favicon uploaded!')
        queryClient.invalidateQueries(['site', id])
        setFaviconFile(null); setFaviconPreview(null)
      },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to upload favicon')
    }
  )

  const uploadLogoMutation = useMutation(
    async (formData) => {
      const uploadRes = await apiService.post('/media/upload', formData)
      const url = uploadRes.data?.files?.[0]?.public_url
      if (!url) throw new Error('No URL returned from upload')
      const rawMeta = site?.data?.meta_data
      const existingMeta = rawMeta ? (typeof rawMeta === 'string' ? JSON.parse(rawMeta) : rawMeta) : {}
      await apiService.put(`/sites/${id}`, { meta_data: JSON.stringify({ ...existingMeta, logo: url }) })
      return url
    },
    {
      onSuccess: () => {
        toast.success('Logo uploaded!')
        queryClient.invalidateQueries(['site', id])
        queryClient.invalidateQueries('sites')
        setLogoFile(null); setLogoPreview(null)
      },
      onError: (e) => toast.error(e.response?.data?.error || e.message || 'Failed to upload logo')
    }
  )

  const removeLogoMutation = useMutation(
    async () => {
      const rawMeta = site?.data?.meta_data
      const existingMeta = rawMeta ? (typeof rawMeta === 'string' ? JSON.parse(rawMeta) : rawMeta) : {}
      const { logo: _removed, ...metaWithoutLogo } = existingMeta
      await apiService.put(`/sites/${id}`, { meta_data: JSON.stringify(metaWithoutLogo) })
    },
    {
      onSuccess: () => { toast.success('Logo removed'); queryClient.invalidateQueries(['site', id]); queryClient.invalidateQueries('sites') },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to remove logo')
    }
  )

  const handleFaviconChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setFaviconFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => setFaviconPreview(ev.target.result)
    reader.readAsDataURL(file)
  }

  const handleFaviconUpload = () => {
    if (!faviconFile) return
    const clientId = site?.data?.client_id
    const formData = new FormData()
    formData.append('favicon', faviconFile)
    if (clientId) formData.append('clientId', clientId)
    uploadFaviconMutation.mutate(formData)
  }

  const handleLogoChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setLogoFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => setLogoPreview(ev.target.result)
    reader.readAsDataURL(file)
  }

  const handleLogoUpload = () => {
    if (!logoFile) return
    const formData = new FormData()
    formData.append('files', logoFile)
    const clientId = site?.data?.client_id
    if (clientId) formData.append('client_id', clientId)
    uploadLogoMutation.mutate(formData)
  }

  const handleSave = () => {
    setIsSaving(true)
    updateSettingsMutation.mutate({ ...settings, gaId })
  }

  const siteUrl = site?.data?.assigned_domain ? `https://${site.data.assigned_domain}` : null

  const copyUrl = () => {
    if (siteUrl) {
      navigator.clipboard.writeText(siteUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1400)
    }
  }

  if (isLoading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 256 }}><LoadingSpinner size="lg" text="Loading site settings…" /></div>
  }

  if (error || !site) {
    return (
      <div className="card card-pad" style={{ border: '1px solid var(--danger)', textAlign: 'center', maxWidth: 500, margin: '48px auto' }}>
        <AlertTriangle style={{ width: 40, height: 40, color: 'var(--danger)', margin: '0 auto 12px' }} />
        <h3 style={{ color: 'var(--danger)', marginBottom: 8 }}>Site not found</h3>
        <p style={{ fontSize: 13, color: 'var(--text-soft)', marginBottom: 16 }}>The requested site could not be found.</p>
        <button className="btn" onClick={() => navigate('/admin/sites')}><ArrowLeft style={{ width: 14, height: 14 }} /> Back to Sites</button>
      </div>
    )
  }

  const rawMeta = site?.data?.meta_data
  const parsedMeta = rawMeta ? (typeof rawMeta === 'string' ? (() => { try { return JSON.parse(rawMeta) } catch { return {} } })() : rawMeta) : {}
  const currentLogo = parsedMeta?.logo || null

  const siteStatus = site?.data?.status
  const statusBadge = siteStatus === 'published'
    ? <span className="badge badge-success"><span className="badge-dot" /> Live</span>
    : siteStatus === 'draft'
    ? <span className="badge badge-warning"><span className="badge-dot" /> Draft</span>
    : <span className="badge"><span className="badge-dot" /> {siteStatus || 'Unknown'}</span>

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <button type="button" className="btn btn-sm" style={{ alignSelf: 'flex-start', marginBottom: 4 }} onClick={() => navigate('/admin/sites')}>
            <ArrowLeft style={{ width: 14, height: 14 }} /> Back
          </button>
          <div className="eyebrow">Sites · Settings</div>
          <h1>Site settings</h1>
          <div className="subtitle">{siteUrl || site?.data?.name} · {site?.data?.company_name}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {siteUrl && (
            <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="btn">
              <ExternalLink style={{ width: 14, height: 14 }} /> Visit site
            </a>
          )}
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving
              ? <><RefreshCw style={{ width: 14, height: 14, animation: 'spin 0.6s linear infinite' }} /> Saving…</>
              : <><Check style={{ width: 14, height: 14 }} /> Save changes</>}
          </button>
        </div>
      </div>

      <div className="page-body" style={{ maxWidth: 820 }}>
        {/* Site information */}
        <CardSection title="Site information" desc="The basics — visible to you and the client." right={statusBadge}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Site name">
              <input className="input" defaultValue={site?.data?.name || ''} readOnly style={{ color: 'var(--text-muted)', cursor: 'not-allowed' }} />
            </Field>
            <Field label="Company">
              <input className="input" defaultValue={site?.data?.company_name || ''} readOnly style={{ color: 'var(--text-muted)', cursor: 'not-allowed' }} />
            </Field>
          </div>
          <Field label="Live URL" full>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ display: 'flex', flex: 1 }}>
                <span style={{ padding: '9px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRight: 0, borderRadius: '10px 0 0 10px', color: 'var(--text-muted)', fontSize: 13, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>https://</span>
                <input className="input" defaultValue={siteUrl?.replace('https://', '') || ''} readOnly style={{ borderRadius: '0 10px 10px 0', color: 'var(--text-muted)', cursor: 'not-allowed' }} />
              </div>
              <button className="btn" style={{ flexShrink: 0 }} onClick={copyUrl} disabled={!siteUrl}>
                {copied ? <><Check style={{ width: 13, height: 13, color: 'var(--success)' }} /> Copied</> : <><Link2 style={{ width: 13, height: 13 }} /> Copy</>}
              </button>
            </div>
            {!siteUrl && <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>No domain assigned yet — go to Domain Management to connect one.</span>}
          </Field>
        </CardSection>

        {/* Favicon */}
        <CardSection title="Favicon" desc="Shown in browser tabs and bookmarks. 32×32 or 64×64 PNG.">
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 16, alignItems: 'stretch' }}>
            <div style={{ borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, padding: 12 }}>
              {(faviconPreview || site?.data?.favicon) ? (
                <img src={faviconPreview || site.data.favicon} alt="Favicon" style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 8 }} />
              ) : (
                <div style={{ width: 44, height: 44, borderRadius: 11, background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontWeight: 700, fontSize: 20 }}>
                  {(site?.data?.name || '?')[0].toUpperCase()}
                </div>
              )}
              <span style={{ fontSize: 10.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>favicon.png</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ height: 90, borderRadius: 12, border: '1.5px dashed var(--border-strong)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', position: 'relative' }}
                onClick={() => document.getElementById('favicon-input').click()}>
                <Upload style={{ width: 18, height: 18, color: 'var(--text-muted)' }} />
                <span style={{ fontSize: 12.5, color: 'var(--text-soft)', fontWeight: 500 }}>Drag & drop, or click to upload</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>PNG · max 1 MB</span>
                <input id="favicon-input" type="file" accept=".ico,.png,.svg" onChange={handleFaviconChange} style={{ display: 'none' }} />
              </div>
              {faviconFile && (
                <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={handleFaviconUpload} disabled={uploadFaviconMutation.isLoading}>
                  {uploadFaviconMutation.isLoading
                    ? <><RefreshCw style={{ width: 12, height: 12, animation: 'spin 0.6s linear infinite' }} /> Uploading…</>
                    : <><Upload style={{ width: 12, height: 12 }} /> Upload favicon</>}
                </button>
              )}
            </div>
          </div>
        </CardSection>

        {/* Site Logo */}
        <CardSection title="Site logo" desc="Displayed on site cards and in the content editor.">
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 16, alignItems: 'stretch' }}>
            <div style={{ borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, padding: 12 }}>
              {(logoPreview || currentLogo) ? (
                <img src={logoPreview || currentLogo} alt="Logo" style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 8 }} />
              ) : (
                <div style={{ width: 44, height: 44, borderRadius: 11, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 12 }}>None</div>
              )}
              {currentLogo && (
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--danger)', padding: '2px 6px' }} onClick={() => removeLogoMutation.mutate()} disabled={removeLogoMutation.isLoading}>
                  {removeLogoMutation.isLoading ? <RefreshCw style={{ width: 11, height: 11, animation: 'spin 0.6s linear infinite' }} /> : <><Trash2 style={{ width: 11, height: 11 }} /> Remove</>}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ height: 90, borderRadius: 12, border: '1.5px dashed var(--border-strong)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer' }}
                onClick={() => document.getElementById('logo-input').click()}>
                <Upload style={{ width: 18, height: 18, color: 'var(--text-muted)' }} />
                <span style={{ fontSize: 12.5, color: 'var(--text-soft)', fontWeight: 500 }}>Upload logo</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>PNG, SVG, WEBP · transparent PNG recommended</span>
                <input id="logo-input" type="file" accept=".png,.jpg,.jpeg,.svg,.webp" onChange={handleLogoChange} style={{ display: 'none' }} />
              </div>
              {logoFile && (
                <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={handleLogoUpload} disabled={uploadLogoMutation.isLoading}>
                  {uploadLogoMutation.isLoading
                    ? <><RefreshCw style={{ width: 12, height: 12, animation: 'spin 0.6s linear infinite' }} /> Uploading…</>
                    : <><Upload style={{ width: 12, height: 12 }} /> Upload logo</>}
                </button>
              )}
            </div>
          </div>
        </CardSection>

        {/* Search engine indexing */}
        <CardSection
          title="Search engine indexing"
          desc="Control whether search engines list this site."
          right={<NSToggle on={settings.searchIndexing} onChange={v => setSettings(p => ({ ...p, searchIndexing: v }))} />}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className={`badge badge-${settings.searchIndexing ? 'success' : 'warning'}`}>
              <span className="badge-dot" /> {settings.searchIndexing ? 'Indexing enabled' : 'Indexing disabled'}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {settings.searchIndexing ? 'Search engines can crawl and list this site.' : 'Search engines are asked not to list this site.'}
            </span>
          </div>
          <Field label="robots.txt" hint="advanced" full>
            <textarea
              className="input mono"
              rows={5}
              style={{ resize: 'vertical', lineHeight: 1.7, fontSize: 12.5 }}
              value={settings.searchIndexing ? `User-agent: *\nAllow: /\n\nSitemap: ${siteUrl || 'https://yoursite.com'}/sitemap.xml` : 'User-agent: *\nDisallow: /'}
              readOnly
            />
          </Field>
        </CardSection>

        {/* Analytics */}
        <CardSection title="Analytics" desc="Connect Google Analytics to surface visitor data.">
          <Field label="Google Analytics measurement ID" full>
            <div style={{ display: 'flex' }}>
              <span style={{ padding: '9px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRight: 0, borderRadius: '10px 0 0 10px', color: 'var(--text-muted)', fontSize: 13, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>G-</span>
              <input className="input" style={{ borderRadius: '0 10px 10px 0' }} value={gaId} onChange={e => setGaId(e.target.value)} placeholder="XXXXXXXXXX" />
            </div>
          </Field>
        </CardSection>

        {/* Maintenance mode */}
        <CardSection title="Maintenance mode" desc="Put the website in maintenance mode while you make updates.">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Enable maintenance mode</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Shows a maintenance page instead of your website</div>
            </div>
            <Switch checked={settings.maintenanceMode} onCheckedChange={checked => setSettings(p => ({ ...p, maintenanceMode: checked }))} />
          </div>
          {settings.maintenanceMode && (
            <Field label="Maintenance message">
              <textarea
                value={settings.maintenanceMessage}
                onChange={e => setSettings(p => ({ ...p, maintenanceMessage: e.target.value }))}
                rows={3}
                className="input"
                style={{ resize: 'none' }}
              />
            </Field>
          )}
        </CardSection>

        {/* Access control */}
        <CardSection title="Access control" desc="Control who can access your website.">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Password protection</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Require a password to access the website</div>
            </div>
            <Switch checked={settings.passwordProtection} onCheckedChange={checked => setSettings(p => ({ ...p, passwordProtection: checked }))} />
          </div>
          {settings.passwordProtection && (
            <Field label="Access password">
              <input type="password" value={settings.password} onChange={e => setSettings(p => ({ ...p, password: e.target.value }))} className="input" placeholder="Enter access password…" />
            </Field>
          )}
        </CardSection>

        {/* Custom code */}
        <CardSection title="Custom code" desc="Add custom HTML, CSS, or JavaScript to your website.">
          <Field label="Header code" hint="before </head>">
            <textarea
              value={settings.customCode.headerCode}
              onChange={e => setSettings(p => ({ ...p, customCode: { ...p.customCode, headerCode: e.target.value } }))}
              rows={4}
              className="input"
              placeholder="<!-- Custom CSS, meta tags, etc. -->"
              style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
          </Field>
          <Field label="Footer code" hint="before </body>">
            <textarea
              value={settings.customCode.footerCode}
              onChange={e => setSettings(p => ({ ...p, customCode: { ...p.customCode, footerCode: e.target.value } }))}
              rows={4}
              className="input"
              placeholder="<!-- Custom JavaScript, tracking codes, etc. -->"
              style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
          </Field>
          <Field label="Analytics code">
            <textarea
              value={settings.customCode.analyticsCode}
              onChange={e => setSettings(p => ({ ...p, customCode: { ...p.customCode, analyticsCode: e.target.value } }))}
              rows={3}
              className="input"
              placeholder="<!-- Google Analytics, Facebook Pixel, etc. -->"
              style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
          </Field>
        </CardSection>

        {/* Danger zone */}
        <CardSection title="Danger zone" desc="These actions affect the live site. Proceed carefully." danger>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, border: '1px solid var(--border)', borderRadius: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500 }}>Unpublish site</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Takes the site offline. You can republish anytime.</div>
            </div>
            <button className="btn btn-sm" style={{ background: 'var(--warning-soft)', color: 'var(--warning)', borderColor: 'transparent' }}
              onClick={async () => {
                const ok = await nsConfirm({
                  tone: 'warning', icon: 'globe',
                  title: 'Unpublish this site?',
                  message: 'The site is taken offline and visitors will no longer be able to reach it. You can republish anytime.',
                  confirmLabel: 'Unpublish',
                })
                if (ok) updateSettingsMutation.mutate({ ...settings, status: 'draft' })
              }}>
              Unpublish
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, border: '1px solid rgba(255,107,107,0.3)', borderRadius: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, color: 'var(--danger)' }}>Delete site</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Permanently removes this site and all its content. Cannot be undone.</div>
            </div>
            <button className="btn btn-sm" style={{ background: 'var(--danger-soft)', color: 'var(--danger)', borderColor: 'transparent' }}
              onClick={async () => {
                const ok = await nsConfirm({
                  tone: 'danger', icon: 'trash',
                  title: 'Delete this site permanently?',
                  message: 'This removes the site and all of its content. Visitors will see a 404 immediately.',
                  detail: 'This cannot be undone',
                  confirmLabel: 'Delete site',
                })
                if (ok) navigate('/admin/sites')
              }}>
              <Trash2 style={{ width: 13, height: 13 }} /> Delete
            </button>
          </div>
        </CardSection>
      </div>
    </>
  )
}

export default SiteSettings
